import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { markdownSanitizeOptions } from '../utils/markdownSanitizeOptions';
import AnalysisProgress from '../components/AnalysisProgress';
import MyDataProgressSteps from '../components/mydata/MyDataProgressSteps';
import {
  authHeader, getToken, isTokenExpired, requestTokenFromApp,
} from '../utils/auth';

// direct 모드에서 백엔드가 보내는 PDF 관련 메시지를 적절한 문구로 교체
function sanitizeDirectMessage(msg) {
  if (!msg || typeof msg !== 'string') return msg;
  return msg
    .replace(/PDF 문서의 내용을 성공적으로 분석하고 저장했습니다/g, '의료 데이터를 불러왔습니다')
    .replace(/PDF 업로드 및 분석을/g, '의료 데이터 분석을')
    .replace(/PDF\s*(문서|업로드|파일)/g, '의료 데이터');
}

export default function AppAnalysisRunningPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const stdPatientId = location.state?.stdPatientId ?? null;

  const [progress, setProgress] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState('');
  const [summaryPreview, setSummaryPreview] = useState('');
  const [reportId, setReportId] = useState(null);
  const [feedbackRating, setFeedbackRating] = useState(null);
  const [feedbackDate, setFeedbackDate] = useState(null);
  const [feedbackSubmitting, setFeedbackSubmitting] = useState(false);
  const [feedbackError, setFeedbackError] = useState(null);

  const startedRef = useRef(false);

  const normalizeProgress = useCallback((items) => {
    if (!Array.isArray(items)) return items;
    const hasBeyondStep = items.some((p) => typeof p?.step === 'number' && p.step > 1);
    if (!hasBeyondStep) return items;
    return items.map((p) => {
      if (p?.step === 1 && p.status !== 'completed' && p.status !== 'failed') {
        const raw = typeof p?.message === 'string'
          ? p.message
              .replace('시작합니다.', '완료되었습니다.').replace('시작합니다', '완료되었습니다')
              .replace('불러오는 중입니다.', '불러왔습니다.').replace('불러오는 중입니다', '불러왔습니다')
          : p?.message;
        return { ...p, status: 'completed', message: sanitizeDirectMessage(raw) };
      }
      return { ...p, message: sanitizeDirectMessage(p?.message) };
    });
  }, []);

  const runAnalysis = useCallback(async (patientId) => {
    try {
      const token = getToken();
      if (token && isTokenExpired(token)) {
        const newToken = await requestTokenFromApp();
        if (!newToken) { window.location.href = '/app/login'; return; }
      }
    } catch (_) {
      window.location.href = '/app/login';
      return;
    }

    setIsLoading(true);
    setError('');
    setResult('');
    setSummaryPreview('');
    setProgress([{ step: 1, status: 'processing', message: '의료 데이터를 불러오는 중입니다.' }]);

    const apiUrl = (process.env.REACT_APP_API_URL ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '') : '');

    // ── WebView(앱) 환경: 백그라운드 submit 방식 ───────────────────────────
    const isInAppWebView = typeof window !== 'undefined' && !!window.ReactNativeWebView;
    if (isInAppWebView) {
      try {
        const response = await fetch(`${apiUrl}/api/v1/analysis/submit-direct`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeader() },
          body: JSON.stringify({ std_patient_id: patientId ? Number(patientId) : null, source: 'app' }),
        });
        const result = await response.json();
        if (!response.ok) {
          throw new Error(result.detail || '분석 요청에 실패했습니다.');
        }
        window.ReactNativeWebView.postMessage(JSON.stringify({
          type: 'ANALYSIS_SUBMITTED',
          jobId: result.job_id,
          status: result.status,
        }));
      } catch (err) {
        setError(err?.message || '분석 요청 중 오류가 발생했습니다.');
        setIsLoading(false);
      }
      return; // 앱 환경에서는 여기서 종료 (SSE 흐름 진입 안 함)
    }

    // ── 일반 브라우저 환경: 기존 SSE 스트리밍 방식 ───────────────────────
    try {
      const response = await fetch(`${apiUrl}/api/v1/analysis/stream-direct`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ std_patient_id: patientId ? Number(patientId) : null }),
      });

      if (!response.ok || !response.body) {
        const detail = await response.json().catch(() => ({}));
        throw new Error(detail.detail || `서버 오류: ${response.statusText}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let buffer = '';

      const processStream = async () => {
        while (true) {
          const { done, value } = await reader.read();
          if (done) { setIsLoading(false); break; }
          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n\n');
          buffer = lines.pop();

          lines.forEach((line) => {
            if (!line.startsWith('data:') && !line.startsWith('event:')) return;
            const eventTypeMatch = line.match(/^event: (.*)/);
            const dataMatch = line.match(/data: (.*)/);
            let eventType = eventTypeMatch ? eventTypeMatch[1].trim() : 'message';
            const dataString = dataMatch ? dataMatch[1].trim() : '{}';
            try {
              const jsonData = JSON.parse(dataString);
              if (eventType === 'message' && jsonData?.event) eventType = jsonData.event;

              if (eventType === 'aidd-complete') {
                setResult(jsonData.final_explanation || '');
                if (typeof jsonData.recommendation_overview === 'string' && jsonData.recommendation_overview.trim()) {
                  setSummaryPreview(jsonData.recommendation_overview);
                }
                setIsLoading(false);
                if (jsonData.report_id) setReportId(jsonData.report_id);
              } else if (eventType === 'error') {
                setError(jsonData.message || '알 수 없는 오류가 발생했습니다.');
                setIsLoading(false);
              } else if (eventType !== 'heartbeat') {
                // step 1 message는 sanitize 처리
                const cleaned = {
                  ...jsonData,
                  message: sanitizeDirectMessage(jsonData.message),
                };
                setProgress((prev) => {
                  const stepExists = prev.some((p) => p.step === cleaned.step);
                  let next;
                  if (stepExists) {
                    next = prev.map((p) => (p.step === cleaned.step ? cleaned : p));
                  } else {
                    next = [...prev, cleaned].sort((a, b) => (a.step || 0) - (b.step || 0));
                  }
                  return normalizeProgress(next);
                });
              }
            } catch (e) {
              console.error('SSE 파싱 오류:', e);
            }
          });
        }
      };

      processStream();
    } catch (err) {
      setError(err?.message || '분석 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  }, [normalizeProgress]);

  useEffect(() => {
    if (stdPatientId === null) {
      const fallback = location.pathname.startsWith('/app') ? '/app/analysis' : '/mydata/connect/consent';
      navigate(fallback, { replace: true });
      return;
    }
    if (startedRef.current) return;
    startedRef.current = true;
    runAnalysis(stdPatientId);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleFeedback = async (rating) => {
    if (feedbackSubmitting || feedbackRating || !reportId) return;
    setFeedbackError(null);
    try {
      setFeedbackSubmitting(true);
      setFeedbackRating(rating);
      const apiUrl = (process.env.REACT_APP_API_URL ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '') : '');
      const res = await fetch(`${apiUrl}/api/v1/reports/${reportId}/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeader() },
        body: JSON.stringify({ rating }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || '피드백 저장 실패');
      }
      const data = await res.json();
      setFeedbackRating(data.feedback_rating || rating);
      setFeedbackDate(data.feedback_date || null);
    } catch (e) {
      setFeedbackError(e?.message || '피드백 저장 중 오류가 발생했습니다.');
    } finally {
      setFeedbackSubmitting(false);
    }
  };

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '24px 16px' }}>

      {/* 전체 흐름 progress indicator */}
      <MyDataProgressSteps current={result ? 3 : 2} />

      {/* 3단계 안내 박스 */}
      <div style={{
        background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 12,
        padding: '16px 18px', marginBottom: 24,
      }}>
        <p style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
          3단계. AI 맞춤 건강 분석
        </p>
        <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, marginBottom: 10 }}>
          수집된 의료 데이터를 기반으로 AI가 개인 맞춤형 건강 리포트를 생성합니다.
          건강 위험 요소를 파악하고 검진 결과를 해석하여 건강관리 가이드를 제공합니다.
        </p>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {['건강 위험 요소 파악', '검진 결과 해석', '맞춤 건강관리 가이드'].map((item) => (
            <span key={item} style={{
              fontSize: 12, padding: '3px 10px', borderRadius: 20,
              background: '#f3f4f6', color: '#374151', fontWeight: 600,
              border: '1px solid #e5e7eb',
            }}>
              {item}
            </span>
          ))}
        </div>
      </div>

      {/* 분석 진행 */}
      {isLoading && (
        <div className="loading-container" style={{ marginTop: 8 }}>
          <AnalysisProgress progress={progress} mode="direct" />
        </div>
      )}

      {/* 에러 */}
      {error && !isLoading && (
        <div style={{
          padding: '14px 16px', borderRadius: 10,
          background: '#fef2f2', border: '1px solid #fecaca',
          color: '#b91c1c', fontSize: 14, marginBottom: 24,
        }}>
          {error}
          <div style={{ marginTop: 12 }}>
            <button
              type="button"
              className="submit-button"
              onClick={() => { startedRef.current = false; runAnalysis(stdPatientId); }}
            >
              다시 시도
            </button>
          </div>
        </div>
      )}

      {/* 결과 — MainPage.js와 동일한 UI 구조 */}
      {!isLoading && result && (
        <div className="result-container" style={{ marginTop: 16, padding: 0, background: 'transparent', border: 'none', boxShadow: 'none', textAlign: 'left', width: '100%' }}>
          <div style={{ maxWidth: '100%', margin: 0 }}>
            <h2 style={{ margin: '0 0 16px 0', fontSize: 24, textAlign: 'left' }}>분석 리포트</h2>

            {summaryPreview && (
              <div style={{ background: '#f9fafb', padding: '12px 16px', borderRadius: '10px', marginBottom: 24, border: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#f3f4f6', color: '#6b7280' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 3v18h18" /><path d="M7 13l3 3 7-7" /></svg>
                  </span>
                  <h3 style={{ margin: 0, color: '#111827', fontSize: 18 }}>한눈에 보는 요약</h3>
                </div>
                <p style={{ margin: '8px 0 0 0', lineHeight: 1.7, whiteSpace: 'pre-line', color: '#374151' }}>{summaryPreview}</p>
              </div>
            )}

            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 22, height: 22, borderRadius: 6, background: '#f3f4f6', color: '#6b7280' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16v16H4z" /><path d="M8 8h8M8 12h8M8 16h6" /></svg>
                </span>
                <h3 style={{ margin: 0, color: '#111827', fontSize: 18 }}>AI 상세 분석 결과</h3>
              </div>
              <div className="markdown-body" style={{ border: '1px solid #dbe2eb', padding: 16, borderRadius: 8, backgroundColor: '#f5f7fa' }}>
                <ReactMarkdown rehypePlugins={[rehypeRaw, [rehypeSanitize, markdownSanitizeOptions]]}>
                  {result}
                </ReactMarkdown>
              </div>
            </div>

            <div className="feedback-section" style={{ marginTop: 28 }}>
              <p>이 분석이 도움이 되셨나요?</p>
              <button
                className="feedback-button"
                onClick={() => handleFeedback('helpful')}
                disabled={feedbackSubmitting || !!feedbackRating}
              >
                👍 도움이 되었어요
              </button>
              <button
                className="feedback-button"
                onClick={() => handleFeedback('needs_improvement')}
                disabled={feedbackSubmitting || !!feedbackRating}
              >
                👎 개선이 필요해요
              </button>
              <div className="feedback-message" style={{ width: '100%', textAlign: 'center', marginTop: 8 }}>
                {feedbackRating && (
                  <span className="submitted-badge" style={{ fontSize: 12, color: '#555' }}>
                    {feedbackRating === 'helpful'
                      ? '도움이 되셨다니 다행이네요. 감사합니다.'
                      : '개선이 필요하다는 의견 감사합니다. 도움이 될 수 있도록 더욱 노력하겠습니다.'}
                    {feedbackDate && (
                      <>
                        <br />
                        <span style={{ fontSize: 11, color: '#777' }}>{new Date(feedbackDate).toLocaleString()}</span>
                      </>
                    )}
                  </span>
                )}
                {feedbackError && (
                  <span className="error-text" style={{ color: '#c00', fontSize: 12 }}>
                    {feedbackError}
                  </span>
                )}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => navigate(location.pathname.startsWith('/app') ? '/app/analysis' : '/')}
                style={{
                  width: '100%', padding: '12px 0', borderRadius: 8, fontSize: 14,
                  border: '1px solid #d1d5db', background: '#f9fafb',
                  color: '#374151', cursor: 'pointer',
                }}
              >
                홈으로 돌아가기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
